/**
 * CardCreateModal — Phase 2b create-card flow for all four deliverable
 * kinds (task, ticket, event, announcement).
 *
 * UX shape:
 *   - Kind selector renders inline at the top as a tab-like radio group
 *     (4 options — too few to deserve a dropdown).
 *   - Kind-specific fields render below. Switching kinds preserves
 *     overlapping fields (title, description, assignee) but clears
 *     fields that don't apply to the new kind.
 *   - The modal is opened from a column header — `defaultStatusId` is
 *     the column the new card will land in.
 *   - Submitting calls the right `useCreateCard(kind)` mutation, then
 *     closes the modal. `useCreateCard` invalidates the board query on
 *     success so the new card appears.
 *
 * The kind-specific schemas mirror the BE Zod input schemas under
 * `3mote-be-new/src/schemas/deliverables/`. See task description for
 * the exact field set.
 *
 * Phase 2b notes:
 *   - `description` uses a plain <TextArea>; TipTap arrives in 2c.
 *   - Ticket `shorthand` is collected from the user as free text (BE
 *     auto-gen is on the backlog).
 *   - Event `endAt` is optional (a "no end time" event).
 *   - Announcement `publishedAt` defaults to "now" the moment the modal
 *     opens, so a default-submit publishes immediately.
 */

import type { Editor } from '@tiptap/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { localizeError } from '@/api/error-codes'
import type { DeliverableKind, Project } from '@/api/types-bridge'
import { Button } from '@/components/Button'
import { Dropdown } from '@/components/Dropdown'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/ToastProvider'
import { MarkdownEditor } from '@/editor/MarkdownEditor'
import { readLiveEditorJson } from '@/editor/live-editor-value'
import {
  failCardFilePlaceholder,
  insertCardFilePlaceholder,
  markCardFilePlaceholderFailed,
  resolveCardFilePlaceholder,
} from '@/editor/nodes/file-embed/card-file-embed'
import {
  failCardImagePlaceholder,
  insertCardImagePlaceholder,
  markCardImagePlaceholderFailed,
  resolveCardImagePlaceholder,
} from '@/editor/nodes/image-embed/card-image-embed'
import {
  failWikiEmbedPlaceholder,
  insertWikiVideoPlaceholder,
  resolveWikiEmbedPlaceholder,
  setWikiEmbedPlaceholderFailed,
} from '@/editor/nodes/wiki-embed-placeholders'
import { isRichTextValueEmpty, parseRichTextValue } from '@/editor/richtext-value'
import { stripInFlightPlaceholderNodes } from '@/editor/strip-inflight-placeholders'
import {
  AnnouncementDatesEditor,
  type DateInputForApi,
} from '@/features/board/components/AnnouncementDatesEditor'
import { RecurrenceEditor, RecurringCheckbox } from '@/features/board/components/RecurrenceEditor'
import { UserMultiPicker } from '@/features/board/components/UserMultiPicker'
import { isHttpUrl } from '@/features/board/lib/join'
import {
  DEFAULT_RECURRENCE_STATE,
  type RecurrenceErrorKey,
  type RecurrenceFormState,
  formStateToRecurrence,
  validateRecurrenceState,
} from '@/features/board/recurrence-utils'
import { useBoard } from '@/features/board/use-board'
import { useCreateCard } from '@/features/board/use-create-card'
import { isEventBearingStatusType, resolveStatusForType } from '@/features/dashboard/status-types'
import { useMe } from '@/features/me/use-me'
import { buildMentionSuggestion } from '@/features/mentions/mention-suggestion'
import { useProjectMembersForMention } from '@/features/mentions/use-project-members-for-mention'
import { useTenantUsers } from '@/features/tenant-settings/use-tenant-users'
import { FileDropOverlay } from '@/features/uploads/FileDropOverlay'
import { PendingAttachmentStrip } from '@/features/uploads/PendingAttachmentStrip'
import { filterReferencedAttachmentIds } from '@/features/uploads/inline-attachment-state'
import { useCardDescriptionDrop } from '@/features/uploads/use-card-description-drop'
import { deletePendingAttachment } from '@/features/uploads/use-file-upload'

import type { ProjectMember } from '@/api/types-bridge'
import { useWorkItemAutodetect } from '@/features/board/use-work-item-autodetect'

interface CardCreateModalProps {
  open: boolean
  onClose: () => void
  /**
   * Initial project context. Pass `''` to open the modal without a
   * pre-selected project — the user picks one from the `projects` list
   * (master-board / sidebar Create-New surface). When non-empty, the
   * project select renders read-only as a label.
   */
  projectId: string
  /**
   * Authoritative project list — required when `projectId` may be `''`
   * so the modal can render its own Project picker. When `projectId` is
   * always non-empty (project-board column header), this can be omitted.
   */
  projects?: readonly Project[]
  /**
   * Column the new card lands in. Empty string when no column is selected
   * — the modal then falls back to the first status of the chosen project
   * (Inbox by convention) on submit. Project-board column headers pass
   * the explicit column id; master-board surfaces leave it empty.
   */
  defaultStatusId: string
  /**
   * FU-E: when set, the kind selector seeds to this kind on open. The
   * tab strip stays interactive so the user can switch kinds; resetting
   * the modal (after submit / close) defaults back to `'task'` per the
   * Phase 2b behaviour.
   */
  defaultKind?: DeliverableKind
  /**
   * Wave 4 Batch K — restrict the kind selector to a subset. Per David's
   * directive, the per-column "+" button only allows Task + Ticket;
   * Events + Announcements are always created from the sidebar's
   * Create-New flow. When omitted, all four kinds remain available
   * (default behaviour, used by the sidebar Create-New entry point).
   *
   * If `defaultKind` is supplied AND not in `allowedKinds`, the seed
   * still wins (the sidebar flow can prefill any kind regardless of
   * downstream `allowedKinds` since the flow itself decides the kind).
   * The tab strip filters to `allowedKinds`; if `allowedKinds` is a
   * single kind the tab strip collapses to a non-interactive label.
   */
  allowedKinds?: ReadonlyArray<DeliverableKind>
}

const KIND_OPTIONS = [
  { value: 'task', labelKey: 'kindLabel.task' },
  { value: 'ticket', labelKey: 'kindLabel.ticket' },
  { value: 'event', labelKey: 'kindLabel.event' },
  { value: 'announcement', labelKey: 'kindLabel.announcement' },
] as const satisfies ReadonlyArray<{ value: DeliverableKind; labelKey: string }>

const PRIORITY_OPTIONS = [
  { value: 'low', labelKey: 'priority.low' },
  { value: 'medium', labelKey: 'priority.medium' },
  { value: 'high', labelKey: 'priority.high' },
  { value: 'urgent', labelKey: 'priority.urgent' },
] as const

type Priority = (typeof PRIORITY_OPTIONS)[number]['value']

const UNASSIGNED_VALUE = '__unassigned__'

/**
 * Convert a `<input type="datetime-local">` value (local-time, no
 * timezone) into a UTC ISO-8601 string for the BE. The BE's Zod schemas
 * call `.datetime()` which requires the trailing `Z` form.
 */
function localDateTimeToIso(local: string): string {
  if (!local) return ''
  // `new Date('2026-04-28T13:30')` interprets it as local time and
  // toISOString() converts to UTC + 'Z'.
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

/** Convert a `<input type="date">` value (yyyy-mm-dd) to UTC ISO. */
function dateOnlyToIso(date: string): string {
  if (!date) return ''
  // Anchor at start-of-day UTC so a "due Apr 30" is unambiguous.
  const d = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

/** Format `new Date()` as `yyyy-MM-ddTHH:mm` for `datetime-local` inputs. */
function nowAsLocalDateTime(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function CardCreateModal({
  open,
  onClose,
  projectId,
  projects,
  defaultStatusId,
  defaultKind,
  allowedKinds,
}: CardCreateModalProps) {
  const { t } = useTranslation('board')
  const { t: tUploads } = useTranslation('uploads')
  // Filter the kind tab strip to the allowed subset. When `allowedKinds`
  // is undefined, all four kinds appear (default behaviour). The seed
  // (`defaultKind`) still wins on initial mount even when not in
  // `allowedKinds` — the sidebar Create-New flow can prefill any kind
  // independent of downstream restrictions.
  const visibleKindOptions = useMemo(() => {
    if (!allowedKinds || allowedKinds.length === 0) return KIND_OPTIONS
    return KIND_OPTIONS.filter((opt) => allowedKinds.includes(opt.value))
  }, [allowedKinds])
  // Local project state — initialized from the prop. When `projects`
  // is provided, the inline dropdown renders so the user can change the
  // target project even when one was pre-selected (David: "if a user's
  // already in a project, the project they're in should be the selected
  // project by default for that work item they're creating, but they
  // should still be able to change it").
  const [selectedProjectId, setSelectedProjectId] = useState(projectId)
  useEffect(() => {
    setSelectedProjectId(projectId)
  }, [projectId])
  const showProjectPicker = projects !== undefined

  const board = useBoard(selectedProjectId)
  const me = useMe()
  const toast = useToast()

  const [kind, setKind] = useState<DeliverableKind>(defaultKind ?? 'task')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  // Task / ticket
  const [priority, setPriority] = useState<Priority>('medium')
  const [assigneeId, setAssigneeId] = useState<string>(UNASSIGNED_VALUE)
  const [dueDate, setDueDate] = useState('')

  // Ticket only

  // Event only
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const [meetingUrl, setMeetingUrl] = useState('')

  // Announcement only
  const [publishedAt, setPublishedAt] = useState('')
  // Per legacy parity (David's directive): announcements never expose a
  // free-form "Published at" field. The user always picks at least one
  // explicit date (Time or Duration mode) — that's the input the on-read
  // coercion uses to bucket the announcement into Inbox / Up Next /
  // Today / Done. No date = unbucketable, so the form blocks submit.
  const [announcementDates, setAnnouncementDates] = useState<DateInputForApi[]>([])

  // Wave 4 Batch K — events + announcements use multi-attendee instead
  // of single assignee. Tracks the selected user-id set for the create
  // payload's `attendeeIds`. Reset on modal close just like every other
  // form field.
  const [attendeeIds, setAttendeeIds] = useState<string[]>([])

  // OS-file drop / toolbar image attachments. No card id yet, so uploads
  // run two-phase: presign with `parentId: undefined`, collect the
  // resulting `attachmentId`s, and hand them to the create mutation which
  // attaches them server-side in the same tenant transaction. `submitted`
  // gates the on-close cleanup so a successful create doesn't delete the
  // rows it just attached.
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const submittedRef = useRef(false)
  /** The ids a successful create bound; every other upload is released. */
  const boundAttachmentIdsRef = useRef<ReadonlySet<string>>(new Set())
  const startedUploadIdsRef = useRef<Set<string>>(new Set())
  /** Release every upload this form created except the ones `keep` names. */
  const releaseUploads = (keep: ReadonlySet<string>) => {
    const ids = new Set([...startedUploadIdsRef.current, ...attachmentIds])
    startedUploadIdsRef.current = new Set()
    for (const id of ids) {
      if (!keep.has(id)) void deletePendingAttachment(id).catch(() => {})
    }
  }
  const descEditorRef = useRef<Editor | null>(null)

  // MOTE-123 — optional recurrence rule on tasks + events. Toggle reveals
  // the editor; state defaults to a sensible weekly Monday rule.
  const [recurringEnabled, setRecurringEnabled] = useState(false)
  const [recurrenceState, setRecurrenceState] =
    useState<RecurrenceFormState>(DEFAULT_RECURRENCE_STATE)
  const [recurrenceErrorKey, setRecurrenceErrorKey] = useState<RecurrenceErrorKey | null>(null)

  const [titleError, setTitleError] = useState<string | null>(null)
  const [startAtError, setStartAtError] = useState<string | null>(null)
  const [meetingUrlError, setMeetingUrlError] = useState<string | null>(null)
  const [publishedAtError, setPublishedAtError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const taskMut = useCreateCard('task', { projectId: selectedProjectId })
  const ticketMut = useCreateCard('ticket', { projectId: selectedProjectId })
  const eventMut = useCreateCard('event', { projectId: selectedProjectId })
  const announcementMut = useCreateCard('announcement', { projectId: selectedProjectId })

  const isPending =
    taskMut.isPending || ticketMut.isPending || eventMut.isPending || announcementMut.isPending

  // Mention autocomplete config — same surface as CardDetailModal so a
  // user can `@` someone in a brand-new description / body.
  const mentionMembers = useProjectMembersForMention(selectedProjectId)
  const mentionMembersRef = useRef(mentionMembers)
  useEffect(() => {
    mentionMembersRef.current = mentionMembers
  }, [mentionMembers])
  const workItemAutodetect = useWorkItemAutodetect()
  const mentionSuggestion = useMemo(
    () =>
      buildMentionSuggestion({
        getItems: (query) => {
          const q = query.trim().toLowerCase()
          const all = mentionMembersRef.current
          if (!q) return all.slice(0, 8)
          return all.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 8)
        },
      }),
    [],
  )

  // Member options come from the board response. Hook returns the
  // unwrapped `BoardResponse` today; FE-5 will switch it to the wrapped
  // `{ data: BoardResponse }` shape — at that point this read just
  // becomes `board.data?.data?.members`.
  const memberOptions = useMemo(() => {
    const members = board.data?.members ?? []
    return [
      { value: UNASSIGNED_VALUE, label: t('cardCreate.assigneeUnassigned') },
      ...members.map((m) => ({ value: m.userId, label: m.name })),
    ]
  }, [board.data, t])

  const descDrop = useCardDescriptionDrop({
    parentKind: kind,
    insertImagePlaceholder: (blobUrl) => insertCardImagePlaceholder(descEditorRef.current, blobUrl),
    resolveImagePlaceholder: (id, result) =>
      resolveCardImagePlaceholder(descEditorRef.current, id, result),
    failImagePlaceholder: (id) => failCardImagePlaceholder(descEditorRef.current, id),
    // Supplying the mark callbacks is what keeps a failed inline upload in
    // the body as a Retry / Dismiss error state instead of removing it —
    // a vanished placeholder reads as "the drop never registered".
    markImagePlaceholderFailed: (id, failed) =>
      markCardImagePlaceholderFailed(descEditorRef.current, id, failed),
    insertFilePlaceholder: (meta) => insertCardFilePlaceholder(descEditorRef.current, meta),
    resolveFilePlaceholder: (id, result) =>
      resolveCardFilePlaceholder(descEditorRef.current, id, result),
    failFilePlaceholder: (id) => failCardFilePlaceholder(descEditorRef.current, id),
    markFilePlaceholderFailed: (id, failed) =>
      markCardFilePlaceholderFailed(descEditorRef.current, id, failed),
    // Same node + attr contract as the wiki video embed (the BE tier and
    // walkers key on `videoEmbed.attrs.fileId`); only the NodeView is
    // card-flavoured. The `wiki` prefix on the helpers is historical.
    insertVideoPlaceholder: (meta) => insertWikiVideoPlaceholder(descEditorRef.current, meta),
    resolveVideoPlaceholder: (id, r) =>
      resolveWikiEmbedPlaceholder(descEditorRef.current, 'videoEmbed', id, {
        fileId: r.attachmentId,
        mimeType: r.mimeType,
        uploadingFileName: null,
      }),
    failVideoPlaceholder: (id) => failWikiEmbedPlaceholder(descEditorRef.current, 'videoEmbed', id),
    markVideoPlaceholderFailed: (id, failed) =>
      setWikiEmbedPlaceholderFailed(descEditorRef.current, 'videoEmbed', id, failed),
    onUploaded: (id) => setAttachmentIds((prev) => [...prev, id]),
    // Every row an upload creates — a failed or cancelled one included —
    // so closing the form can release all of them, not just the finished.
    onUploadStarted: (id) => {
      startedUploadIdsRef.current.add(id)
    },
  })

  const reset = () => {
    // Best-effort discard of any uploads that never made it onto a card
    // (modal cancelled / ESC / backdrop close before submit). A successful
    // create sets `submittedRef` so the attached rows survive.
    releaseUploads(submittedRef.current ? boundAttachmentIdsRef.current : new Set())
    submittedRef.current = false
    boundAttachmentIdsRef.current = new Set()
    setAttachmentIds([])
    descDrop.reset()
    // When `allowedKinds` is specified, default to its first entry so we
    // never reset to a kind the user can't pick (e.g. defaulting to
    // 'task' when only ['event', 'announcement'] are allowed).
    const fallbackKind = allowedKinds && allowedKinds.length > 0 ? allowedKinds[0] : 'task'
    setKind(fallbackKind ?? 'task')
    setTitle('')
    setDescription('')
    setPriority('medium')
    setAssigneeId(UNASSIGNED_VALUE)
    setDueDate('')
    setStartAt('')
    setEndAt('')
    setMeetingUrl('')
    setPublishedAt('')
    setAnnouncementDates([])
    setAttendeeIds([])
    setRecurringEnabled(false)
    setRecurrenceState(DEFAULT_RECURRENCE_STATE)
    setRecurrenceErrorKey(null)
    setTitleError(null)
    setStartAtError(null)
    setMeetingUrlError(null)
    setPublishedAtError(null)
    setFormError(null)
  }

  // Switching the kind tab re-scopes the description drop's presign to the
  // new kind. Attachments already uploaded under the OLD kind can't be
  // attached to a card of a different kind (the create route's parent-kind
  // guard would reject them and roll back the whole create), so discard
  // them (best-effort) and clear the pending strip before the switch.
  const handleKindChange = (next: DeliverableKind) => {
    if (next === kind) return
    releaseUploads(new Set())
    setAttachmentIds([])
    descDrop.reset()
    setKind(next)
  }

  // Default `publishedAt` to "now" each time the modal opens so an
  // announcement submission posts immediately by default.
  useEffect(() => {
    if (open) {
      setPublishedAt(nowAsLocalDateTime())
    }
  }, [open])

  // FU-E: when the sidebar's Create-New picker passes a kind preset, sync
  // the local `kind` state on every open. Re-runs when the lock changes
  // so reopening with a different kind works without a full unmount.
  useEffect(() => {
    if (open && defaultKind) {
      setKind(defaultKind)
    }
  }, [open, defaultKind])

  // Reset when the modal closes so the next open is a clean slate.
  // Capturing this in an effect (instead of in handleClose) lets ESC
  // and backdrop-close paths reset too.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reset` only calls stable React setState dispatchers; intentionally re-running on `open` toggle alone.
  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open])

  const handleClose = () => {
    if (isPending) return
    onClose()
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setTitleError(null)
    setStartAtError(null)
    setPublishedAtError(null)
    setFormError(null)

    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setTitleError(t('cardCreate.errorTitleRequired'))
      return
    }

    // Wave 4 Batch K — announcements are tenant-wide. The project picker
    // is optional for the announcement kind; when omitted, the BE treats
    // the announcement as `projectId: null` and surfaces it in every
    // project's board AND on the master dashboard. Every other kind
    // still requires a project.
    const requiresProject = kind !== 'announcement'
    if (requiresProject && !selectedProjectId) {
      setFormError(t('cardCreate.errorSelectProject'))
      return
    }

    // Status resolution: prefer the explicit `defaultStatusId` when the
    // modal was opened from a column header. When the modal is opened
    // from the master-board / sidebar surface, fall back to the first
    // status of the chosen project (Inbox by convention). For tenant-
    // wide announcements (no project selected), no status is resolved —
    // the BE picks the appropriate per-project status during the
    // tenant-wide-fanout.
    //
    // Events + announcements are column-locked at the BE to
    // {Up Next, Today, Done}. If the modal was opened from a forbidden
    // column (e.g. Inbox / Review) we'd 400. Coerce to "Up Next" by
    // default — semantically "scheduled for the future" — and let the
    // on-read coercion rebucket to Today on the actual day, then Done
    // after the end. Defaulting to Today would be wrong for anything
    // more than 24h out (derive returns null beyond that window and the
    // card would stay parked in Today even though it isn't today).
    // Allowed destinations are resolved by column TYPE, not name — a
    // project that renamed `Up Next` (or added a second up_next-typed
    // column) keeps working, where a name match would fall through to
    // `allowed[0]` and park the card in the wrong column.
    const isTimeBucketed = kind === 'event' || kind === 'announcement'
    let effectiveStatusId: string
    if (isTimeBucketed && board.data?.statuses) {
      const allowed = board.data.statuses.filter((s) => isEventBearingStatusType(s.columnType))
      const fromDefault =
        defaultStatusId && allowed.some((s) => s.id === defaultStatusId) ? defaultStatusId : null
      const upNext = resolveStatusForType(allowed, 'up_next')?.id
      effectiveStatusId = fromDefault ?? upNext ?? allowed[0]?.id ?? ''
    } else {
      effectiveStatusId =
        defaultStatusId && board.data?.statuses.some((s) => s.id === defaultStatusId)
          ? defaultStatusId
          : (board.data?.statuses[0]?.id ?? '')
    }

    // Tenant-wide announcements may submit without a status (no project
    // selected → no status catalog to pick from). Other kinds still
    // require one.
    if (!effectiveStatusId && requiresProject) {
      setFormError(
        board.isLoading ? t('cardCreate.errorLoadingColumns') : t('cardCreate.errorNoColumns'),
      )
      return
    }

    const resolvedAssignee = assigneeId === UNASSIGNED_VALUE ? null : assigneeId
    // Canonical rich-text (ADR-039): the editor emits a serialized TipTap
    // doc — send the parsed object; a visually-empty doc collapses to
    // `undefined` (same semantics the old `.trim() || undefined` had for
    // markdown strings).
    // The strip drops any upload placeholder still sitting in the body. A
    // failed inline upload now STAYS put as an error state the author can
    // retry, so without it a create would persist a node whose only source
    // is a blob URL that dies with the tab.
    // Read the LIVE editor: its onChange is debounced, so a quick Create
    // would otherwise send the previous body — and bind an embed the
    // author has just deleted.
    const liveDescription = readLiveEditorJson(descEditorRef.current, description)
    const descriptionParsed = isRichTextValueEmpty(liveDescription)
      ? undefined
      : parseRichTextValue(liveDescription)
    const descriptionValue =
      descriptionParsed === undefined || typeof descriptionParsed === 'string'
        ? descriptionParsed
        : stripInFlightPlaceholderNodes(descriptionParsed)

    // Two-phase attachments — dropped / toolbar-uploaded files were
    // presigned without a parent; hand the create route the ids the
    // description still shows so it binds them in the same transaction.
    // The rest (a Backspaced embed, a failed attempt) are released once
    // the card exists.
    const referencedAttachmentIds =
      descriptionValue === undefined
        ? []
        : filterReferencedAttachmentIds(attachmentIds, descriptionValue)
    const attachmentIdsPayload =
      referencedAttachmentIds.length > 0 ? referencedAttachmentIds : undefined

    // MOTE-123 — validate the recurrence rule before submit when enabled.
    // Tasks + events only; tickets + announcements never carry a rule.
    setRecurrenceErrorKey(null)
    const wantsRecurrence = recurringEnabled && (kind === 'task' || kind === 'event')
    if (wantsRecurrence) {
      const errKey = validateRecurrenceState(recurrenceState)
      if (errKey) {
        setRecurrenceErrorKey(errKey)
        return
      }
    }
    const recurrencePayload = wantsRecurrence ? formStateToRecurrence(recurrenceState) : undefined

    try {
      let createdLabel: string
      if (kind === 'task') {
        await taskMut.mutateAsync({
          projectId: selectedProjectId,
          statusId: effectiveStatusId,
          title: trimmedTitle,
          description: descriptionValue,
          priority,
          assigneeId: resolvedAssignee,
          dueAt: dueDate ? dateOnlyToIso(dueDate) : null,
          recurrence: recurrencePayload,
          attachmentIds: attachmentIdsPayload,
        })
        createdLabel = t('cardCreate.createdTask')
      } else if (kind === 'ticket') {
        // BE auto-assigns the shorthand (e.g. `MOTE-101`) on create — the
        // legacy free-text input was dropped in Wave 1 of the parity sweep.
        // `reporter` is REQUIRED — both the BE Zod schema in
        // `3mote-be-new/src/schemas/deliverables/tickets.ts` and the FE
        // `CreateTicketRequest` interface have it non-optional. Bail
        // early with an inline form error if the auth context isn't
        // resolvable (rare; would mean the `useMe()` query hasn't settled
        // or returned no user). The conditional-spread alternative
        // (`...(reporterId ? { reporter } : {})`) silently 400s on the
        // BE because the BE doesn't default it from the JWT.
        const reporterId = me.data?.user.id
        if (!reporterId) {
          setFormError(t('cardCreate.errorCouldNotResolveUser'))
          return
        }
        await ticketMut.mutateAsync({
          projectId: selectedProjectId,
          statusId: effectiveStatusId,
          title: trimmedTitle,
          description: descriptionValue,
          priority,
          assigneeId: resolvedAssignee,
          reporter: reporterId,
          dueAt: dueDate ? dateOnlyToIso(dueDate) : null,
          attachmentIds: attachmentIdsPayload,
        })
        createdLabel = t('cardCreate.createdTicket')
      } else if (kind === 'event') {
        if (!startAt) {
          setStartAtError(t('cardCreate.errorStartTimeRequired'))
          return
        }
        const startIso = localDateTimeToIso(startAt)
        if (!startIso) {
          setStartAtError(t('cardCreate.errorInvalidStartTime'))
          return
        }
        const endIso = endAt ? localDateTimeToIso(endAt) : null
        const trimmedMeetingUrl = meetingUrl.trim()
        // Mirror the BE's http(s)-only rule so a bad paste gets a readable
        // message instead of a raw 400 on submit.
        if (trimmedMeetingUrl && !isHttpUrl(trimmedMeetingUrl)) {
          setMeetingUrlError(t('cardCreate.errorInvalidMeetingLink'))
          return
        }
        // Wave 4 Batch K — events accept `attendeeIds: string[]` instead
        // of a single `assigneeId`. The legacy single-host model is gone;
        // every event has a creator (BE-stamped from the auth token) +
        // an attendee set the modal collects via `<UserMultiPicker>`.
        await eventMut.mutateAsync({
          projectId: selectedProjectId,
          statusId: effectiveStatusId,
          title: trimmedTitle,
          description: descriptionValue,
          startAt: startIso,
          endAt: endIso,
          meetingUrl: trimmedMeetingUrl || null,
          attendeeIds,
          recurrence: recurrencePayload,
          attachmentIds: attachmentIdsPayload,
        })
        createdLabel = t('cardCreate.createdEvent')
      } else {
        // announcement — legacy parity: at least one date is required.
        // The on-read coercion uses `dates` to bucket into Inbox / Up
        // Next / Today / Done; no dates → permanently stuck in Inbox.
        // Block at the form level so the row is always bucketable.
        if (announcementDates.length === 0) {
          setPublishedAtError(t('cardCreate.errorAtLeastOneDate'))
          return
        }
        // Time-mode rows must have a start time; duration-mode rows
        // must have hours/minutes set. The editor doesn't validate
        // (it's a controlled-input shape), so do it here.
        const incomplete = announcementDates.find((d) => {
          if (d.entryType === 'time') return !d.date || !d.startTime
          // duration: date is required; hours OR minutes must be > 0
          return !d.date || (!d.hours && !d.minutes)
        })
        if (incomplete) {
          setPublishedAtError(t('cardCreate.errorIncompleteDateRow'))
          return
        }
        // Wave 4 Batch K — announcements are tenant-wide. `projectId`
        // is omitted (undefined) when no project is selected so the BE
        // treats the announcement as org-wide and surfaces it on every
        // project's board + the master dashboard. `attendeeIds` carries
        // the multi-user set (replacing the single-assignee legacy
        // shape). `publishedAt` is set to "now" — the BE keeps the
        // column on the legacy field for back-compat but the FE no
        // longer surfaces it (legacy parity).
        await announcementMut.mutateAsync({
          projectId: selectedProjectId || undefined,
          statusId: effectiveStatusId || undefined,
          title: trimmedTitle,
          body: descriptionValue,
          publishedAt: new Date().toISOString(),
          dates: announcementDates,
          attendeeIds,
          attachmentIds: attachmentIdsPayload,
        })
        createdLabel = t('cardCreate.createdAnnouncement')
      }

      // Mark submitted so the on-close reset keeps the attachments that
      // were just handed to the create mutation instead of deleting them.
      submittedRef.current = true
      boundAttachmentIdsRef.current = new Set(referencedAttachmentIds)
      toast.success(createdLabel)
      onClose()
    } catch (err) {
      // `localizeError` resolves mapped BE codes (e.g. `richtext_invalid`)
      // through the errors catalog and falls back to the envelope /
      // error message for unmapped ones — same behavior `err.message`
      // gave for those, now localized where a mapping exists.
      const message =
        err instanceof Error ? localizeError(err, t) : t('cardCreate.errorCouldNotCreate')
      setFormError(message)
      toast.error(message)
    }
  }

  return (
    <Modal open={open} onClose={handleClose} ariaLabel={t('cardCreate.ariaLabel')} size="2xl">
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header>{t('cardCreate.title')}</Modal.Header>
        <Modal.Body className="flex max-h-[75vh] flex-col overflow-hidden !p-0">
          {/* Kind picker — full-width 48px tab strip with underline
              selection (same visual language as the Details / Notes
              tabs). Only renders when MORE than one kind is available;
              when `allowedKinds` collapses to a single kind the modal
              is effectively kind-locked and the strip is empty UI. */}
          {visibleKindOptions.length > 1 && (
            <div
              role="radiogroup"
              aria-label={t('cardCreate.kindGroupAriaLabel')}
              className="flex h-10 flex-none items-stretch border-b border-border px-6"
            >
              <div className="-mb-px flex gap-6">
                {visibleKindOptions.map((option) => {
                  const selected = option.value === kind
                  const base =
                    'flex h-full cursor-pointer items-center px-1 text-sm transition-colors focus:outline-none border-b-[3px] disabled:cursor-not-allowed disabled:opacity-60'
                  const activeClasses = 'border-accent text-fg font-semibold'
                  const idleClasses =
                    'border-transparent text-fg-muted font-medium hover:text-fg focus-visible:text-fg'
                  return (
                    <button
                      key={option.value}
                      type="button"
                      // biome-ignore lint/a11y/useSemanticElements: tab-like radio group; native <input type="radio"> can't render the underline-tab UI.
                      role="radio"
                      aria-checked={selected}
                      onClick={() => handleKindChange(option.value)}
                      disabled={isPending}
                      className={`${base} ${selected ? activeClasses : idleClasses}`}
                    >
                      {t(option.labelKey)}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Scrollable form area — independent scroll container so the
              tab strip stays pinned at the top when the form is long
              (announcements with multiple date rows, etc.). */}
          <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
            <FormField
              id="card-create-title"
              label={t('cardCreate.fieldTitle')}
              placeholder={t('cardCreate.titlePlaceholder')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              error={titleError ?? undefined}
              required
              maxLength={500}
              disabled={isPending}
            />

            {/*
             * Project picker — only renders when the modal opened without
             * a pre-selected project (master-board / sidebar Create-New
             * surface). Project-board column headers pass an explicit
             * `projectId` and skip this field entirely. Matches the legacy
             * site's "Select Project" affordance inside every kind modal.
             */}
            {showProjectPicker && projects && (
              <div className="flex flex-col gap-1">
                <label htmlFor="card-create-project" className="text-sm font-medium text-fg">
                  {t('cardCreate.labelProject')}{' '}
                  {kind === 'announcement' ? (
                    <span className="text-xs font-normal text-fg-muted">
                      {t('cardCreate.projectOptional')}
                    </span>
                  ) : (
                    <span className="text-rose-500">*</span>
                  )}
                </label>
                <Dropdown
                  id="card-create-project"
                  value={selectedProjectId}
                  onChange={(value) => setSelectedProjectId(value)}
                  options={[
                    {
                      value: '',
                      label:
                        kind === 'announcement'
                          ? t('cardCreate.projectOrgWide')
                          : t('cardCreate.projectSelect'),
                    },
                    ...projects
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => ({ value: p.id, label: p.name })),
                  ]}
                  disabled={isPending}
                />
              </div>
            )}

            <div className="flex flex-col gap-2" {...descDrop.dropHandlers}>
              <label htmlFor="card-create-description" className="text-sm font-medium text-fg">
                {kind === 'announcement'
                  ? t('cardCreate.labelBody')
                  : t('cardCreate.labelDescription')}
              </label>
              {/* The overlay anchors to this editor-only wrapper (NOT the
                  outer block) so it frames the description field exactly,
                  not the label above it. Drop handlers stay on the outer
                  block so a drop anywhere in the section still lands. */}
              <div className="relative">
                <MarkdownEditor
                  value={description}
                  // Canonical rich-text (ADR-039): create-form descriptions
                  // serialize as TipTap JSON from the first keystroke.
                  valueFormat="json"
                  onChange={setDescription}
                  ariaLabel={
                    kind === 'announcement'
                      ? t('cardCreate.ariaAnnouncementBody')
                      : t('cardCreate.ariaCardDescription')
                  }
                  minRows={3}
                  maxRows={12}
                  mentionSuggestion={mentionSuggestion}
                  workItemAutodetect={workItemAutodetect}
                  // Attach button + OS-file drop share one path (two-phase
                  // pending attach; ids collected for the create call).
                  onAttachFiles={descDrop.acceptFiles}
                  onEditorReady={(editor) => {
                    descEditorRef.current = editor
                  }}
                />
                {descDrop.isDragActive && (
                  <FileDropOverlay
                    label={tUploads('dropCard.title')}
                    hint={tUploads('dropCard.hintEmbeds')}
                  />
                )}
              </div>
              <PendingAttachmentStrip
                items={descDrop.pendingItems}
                onRemove={descDrop.removePending}
                ariaLabel={tUploads('dropCard.pendingAria')}
              />
            </div>

            {(kind === 'task' || kind === 'ticket') && (
              <Dropdown
                id="card-create-priority"
                label={t('cardCreate.labelPriority')}
                value={priority}
                onChange={(value) => setPriority(value as Priority)}
                options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
                disabled={isPending}
              />
            )}

            {kind === 'event' && (
              <>
                <div className="flex flex-col gap-1">
                  <label htmlFor="card-create-start-at" className="text-sm font-medium text-fg">
                    {t('cardCreate.labelStartsAt')}
                  </label>
                  <input
                    id="card-create-start-at"
                    type="datetime-local"
                    value={startAt}
                    onChange={(event) => setStartAt(event.target.value)}
                    required
                    disabled={isPending}
                    className="block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
                  />
                  {startAtError && <p className="text-xs text-danger">{startAtError}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="card-create-end-at" className="text-sm font-medium text-fg">
                    {t('cardCreate.labelEndsAt')}
                  </label>
                  <input
                    id="card-create-end-at"
                    type="datetime-local"
                    value={endAt}
                    onChange={(event) => setEndAt(event.target.value)}
                    disabled={isPending}
                    className="block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="card-create-meeting-url" className="text-sm font-medium text-fg">
                    {t('cardCreate.labelMeetingLink')}
                  </label>
                  <input
                    id="card-create-meeting-url"
                    type="url"
                    inputMode="url"
                    value={meetingUrl}
                    placeholder={t('cardCreate.meetingLinkPlaceholder')}
                    onChange={(event) => {
                      setMeetingUrl(event.target.value)
                      if (meetingUrlError) setMeetingUrlError(null)
                    }}
                    disabled={isPending}
                    className="block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
                  />
                  {meetingUrlError && <p className="text-xs text-danger">{meetingUrlError}</p>}
                </div>
              </>
            )}

            {kind === 'announcement' && (
              <div className="flex flex-col gap-1">
                {/* Legacy parity: Choose Date Type (Time | Duration), one
                  or more date rows, each with a date + start/end time
                  OR hours/minutes. The on-read coercion uses these to
                  bucket the announcement — today's date → Today,
                  tomorrow → Up Next, further out → Inbox. The legacy
                  UI never showed a separate "Published at" field;
                  we don't either. */}
                {/* Section heading, not a form label — `AnnouncementDatesEditor`
                  is a composite widget without a single labelable input. */}
                <span className="text-sm font-medium text-fg">{t('cardCreate.labelDates')}</span>
                <AnnouncementDatesEditor rows={[]} onChange={setAnnouncementDates} />
                {publishedAtError && <p className="text-xs text-danger">{publishedAtError}</p>}
                {/* Reference the unused setPublishedAt to keep TS happy
                  while the legacy `publishedAt` state remains in case a
                  follow-up wants the BE to default it differently. */}
                <span className="hidden">{publishedAt}</span>
              </div>
            )}

            {(kind === 'task' || kind === 'ticket') && (
              <Dropdown
                id="card-create-assignee"
                label={t('cardCreate.labelAssignee')}
                value={assigneeId}
                onChange={setAssigneeId}
                options={memberOptions}
                disabled={isPending || memberOptions.length <= 1}
              />
            )}

            {/* Wave 4 Batch K — events + announcements collect attendees
              instead of a single assignee. Multi-select user picker.
              Source of the candidate list switches by surface:
                - Events: project members (events stay project-scoped).
                - Announcements with a project: project members.
                - Org-wide announcements: every user in the tenant
                  (`useTenantUsers`) — the announcement broadcasts to
                  any subset of the org, so the picker has to surface
                  the whole roster, not just one project's members. */}
            {(kind === 'event' || kind === 'announcement') && (
              <AttendeesField
                kind={kind}
                selectedProjectId={selectedProjectId}
                projectMembers={board.data?.members ?? []}
                selectedUserIds={attendeeIds}
                onChange={setAttendeeIds}
              />
            )}

            {(kind === 'task' || kind === 'ticket') && (
              <div className="flex flex-col gap-1">
                <label htmlFor="card-create-due-date" className="text-sm font-medium text-fg">
                  {t('cardCreate.labelDueDate')}
                </label>
                <input
                  id="card-create-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  disabled={isPending}
                  className="block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
                />
              </div>
            )}

            {/* MOTE-123 — recurrence (tasks + events only). Tickets +
              announcements never carry a rule. Per design: bold section
              header → styled checkbox + "Recurring" label → editor
              expands inline when checked. */}
            {(kind === 'task' || kind === 'event') && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-semibold text-fg">
                  {t('recurrence.sectionHeader', {
                    item:
                      kind === 'task'
                        ? t('recurrence.itemTypeProper.task')
                        : t('recurrence.itemTypeProper.event'),
                  })}
                </span>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: <RecurringCheckbox> renders a real <input type="checkbox"> inside; the label wraps the visible-text + the styled checkbox so clicking anywhere toggles. */}
                <label className="inline-flex w-fit cursor-pointer items-center gap-2 text-sm text-fg">
                  <RecurringCheckbox
                    checked={recurringEnabled}
                    onChange={(next) => {
                      setRecurringEnabled(next)
                      if (!next) setRecurrenceErrorKey(null)
                    }}
                    disabled={isPending}
                  />
                  {t('recurrence.checkboxLabel')}
                </label>
                {recurringEnabled && (
                  <RecurrenceEditor
                    value={recurrenceState}
                    onChange={setRecurrenceState}
                    itemType={kind}
                    disabled={isPending}
                    error={recurrenceErrorKey ? t(recurrenceErrorKey) : null}
                  />
                )}
              </div>
            )}

            {formError && (
              <p role="alert" className="text-xs text-danger">
                {formError}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
            {t('cardCreate.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? t('cardCreate.submitCreating') : t('cardCreate.submitCreate')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/*                              AttendeesField                                */
/* -------------------------------------------------------------------------- */

/**
 * Attendee multi-select scoped by surface:
 *   - Project-scoped (event always; announcement when a project is
 *     picked): pulls from the per-project board's `members` list. Same
 *     shape `UserMultiPicker` has expected since Phase 3.
 *   - Org-wide (announcement with no project): pulls from
 *     `useTenantUsers()`. Tenant users are mapped into the
 *     `ProjectMember` shape `UserMultiPicker` consumes (`{ userId, name,
 *     email, avatarUrl, role }`). The fake `role` here is a display
 *     concession — `ProjectMember.role` is the project-scoped role; for
 *     a tenant-wide picker we use the tenant `roleSlug` instead, cast
 *     into the same union. The picker only displays it as a label hint,
 *     so the cast is benign.
 *
 * Future: a "Teams" tab inside the picker for selecting whole teams.
 * Filed as a follow-up; org-wide announcements need this for "broadcast
 * to India team" use case. For v1 it's per-user only.
 */
function AttendeesField({
  kind,
  selectedProjectId,
  projectMembers,
  selectedUserIds,
  onChange,
}: {
  kind: 'event' | 'announcement'
  selectedProjectId: string
  projectMembers: readonly ProjectMember[]
  selectedUserIds: string[]
  onChange: (next: string[]) => void
}) {
  const { t } = useTranslation('board')
  const isOrgWideAnnouncement = kind === 'announcement' && !selectedProjectId
  // Only fetch tenant users when we actually need them — the per-project
  // path uses `projectMembers` and a tenant-wide fetch would be wasted.
  const tenantUsers = useTenantUsers()
  const orgWideMembers = useMemo<ProjectMember[]>(() => {
    if (!isOrgWideAnnouncement) return []
    return (tenantUsers.data ?? []).map((u) => ({
      userId: u.id,
      name: u.name,
      email: u.email,
      avatarUrl: u.avatarUrl,
      kind: u.kind ?? 'human',
      // Tenant role slug carried through into the project-role slot;
      // the picker only renders this as a chip label hint.
      role: u.roleSlug as ProjectMember['role'],
    }))
  }, [isOrgWideAnnouncement, tenantUsers.data])

  const members = isOrgWideAnnouncement ? orgWideMembers : projectMembers
  const isLoading = isOrgWideAnnouncement && tenantUsers.isLoading
  const placeholder = isOrgWideAnnouncement
    ? isLoading
      ? t('cardCreate.attendeesLoadingOrg')
      : t('cardCreate.attendeesAddOrg')
    : t('cardCreate.attendeesAdd')

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="card-create-attendees" className="text-sm font-medium text-fg">
        {t('cardCreate.labelAttendees')}
      </label>
      <UserMultiPicker
        members={members}
        selectedUserIds={selectedUserIds}
        onChange={onChange}
        placeholder={placeholder}
      />
      {isOrgWideAnnouncement && (
        <p className="text-xs text-fg-muted">{t('cardCreate.attendeesOrgHelp')}</p>
      )}
    </div>
  )
}
